import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { icons } from "../../utils/icons";
import { useServices } from "../../hooks/useServices";
import MarketingSlides from "../../components/feature/MarketingSlides";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

// ── All services grouped by category ─────────────────────────────────────
// Each group has an id used to filter visibility against isEnabled() flags.
// serviceKey: null  = always visible
// serviceKey: string = only shown if isEnabled(serviceKey)
const SERVICE_GROUPS = [
  {
    title: "Bills & Payments",
    id: "bills",
    services: [
      { label: "Airtime",     icon: "ri-phone-line",       tone: "success", path: "/bills/airtime" },
      { label: "Data",        icon: "ri-wifi-line",         tone: "info",    path: "/bills/data" },
      { label: "Electricity", icon: "ri-flashlight-line",   tone: "warning", path: "/bills/electricity" },
      { label: "Cable TV",    icon: "ri-tv-2-line",         tone: "purple",  path: "/bills/tv" },
    ],
  },
  {
    title: "Money Transfers",
    id: "transfers",
    services: [
      { label: "Send Money",   icon: "ri-send-plane-line",  tone: "brand",   path: "/internal-transfer" },
      { label: "To Bank",      icon: "ri-bank-line",         tone: "info",    path: "/withdraw-naira",   serviceKey: "naira_withdrawal" },
      { label: "Payment Link", icon: "ri-links-line",        tone: "purple",  path: "/payment-link" },
      { label: "Fund Wallet",  icon: "ri-bank-card-line",    tone: "success", path: "/deposit-fiat",     serviceKey: "fiat_deposit" },
    ],
  },
  {
    title: "Business",
    id: "business",
    services: [
      { label: "Company",  icon: "ri-building-2-line", tone: "warning", path: "/business" },
      // { label: "Payroll",  icon: "ri-team-line",       tone: "info",    path: "/business" },
      { label: "Goals",    icon: "ri-flag-line",        tone: "success", path: "/savings" },
    ],
  },
  {
    title: "Savings & Rewards",
    id: "savings",
    services: [
      { label: "Target Savings", icon: "ri-safe-line",   tone: "success", path: "/savings" },
      { label: "Rewards",        icon: "ri-gift-line",   tone: "warning", path: "/rewards",                serviceKey: "rewards" },
      { label: "Referral",       icon: "ri-team-line",   tone: "purple",  path: "/rewards?tab=referral",   serviceKey: "rewards" },
    ],
  },
  {
    title: "Crypto & Trading",
    id: "trading",
    serviceKey: "trading",    // entire group hidden if trading is disabled
    services: [
      { label: "Buy Crypto",      icon: "ri-swap-box-line",          tone: "info",    path: "/buy" },
      { label: "Sell Crypto",     icon: "ri-exchange-dollar-line",   tone: "danger",  path: "/sell" },
      { label: "My Holdings",     icon: "ri-wallet-3-line",          tone: "brand",   path: "/wallet" },
      { label: "Deposit Crypto",  icon: "ri-arrow-down-circle-line", tone: "purple",  path: "/deposit",         serviceKey: "deposits" },
      { label: "Withdraw Crypto", icon: "ri-arrow-up-circle-line",   tone: "warning", path: "/withdraw-crypto", serviceKey: "withdrawals" },
    ],
  },
  {
    title: "Cards",
    id: "cards",
    services: [
      { label: "Virtual Card", icon: "ri-bank-card-line", tone: "info", path: "/card", badge: "SOON" },
    ],
  },
  {
    title: "Account & Settings",
    id: "account",
    services: [
      { label: "Profile",        icon: "ri-user-line",              tone: "muted",   path: "/profile" },
      { label: "Saved Banks",    icon: "ri-bank-line",              tone: "info",    path: "/saved-banks" },
      { label: "Transactions",   icon: "ri-file-list-3-line",       tone: "success", path: "/transactions" },
      { label: "Notifications",  icon: "ri-notification-3-line",    tone: "warning", path: "/profile/notifications" },
      { label: "Support",        icon: "ri-customer-service-2-line",tone: "warning", path: "/support" },
    ],
  },
];

const QUICK_PROVIDERS = [
  { label: "MTN",   img: icons.mtn,   path: "/bills/airtime?network=MTN" },
  { label: "DSTV",  img: icons.dstv,  path: "/bills/tv?provider=DSTV" },
  { label: "EKEDC", img: icons.ekedc, path: "/bills/electricity?disco=EKEDC" },
  { label: "Glo",   img: icons.glo,   path: "/bills/data?network=GLO" },
  { label: "Airtel",img: icons.airtel,path: "/bills/airtime?network=AIRTEL" },
  { label: "GOtv",  img: icons.gotv,  path: "/bills/tv?provider=GOTV" },
];

// ── Single service icon tile ──────────────────────────────────────────────
function ServiceTile({ service, onNavigate }) {
  const isMuted = !service.tone;
  return (
    <button onClick={() => onNavigate(service.path)}
      className="flex flex-col items-center gap-2 bg-surface rounded-2xl py-3.5 px-1 border border-border shadow-card hover:border-brand-border active:scale-95 transition-all relative">
      {service.badge && (
        <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full text-white z-10"
          style={{ background: "var(--color-danger)" }}>{service.badge}</span>
      )}
      <div className="w-11 h-11 rounded-xl flex items-center justify-center"
        style={{
          background: isMuted ? "var(--color-surface-muted)" : `var(--color-${service.tone}-light)`,
          boxShadow: isMuted ? "none" : `inset 0 0 0 1px var(--color-${service.tone}-ring)`,
        }}>
        <i className={`${service.icon} text-xl`}
          style={{ color: isMuted ? "var(--color-text-muted)" : `var(--color-${service.tone})` }} />
      </div>
      <span className="text-secondary text-[10px] font-semibold text-center leading-tight">{service.label}</span>
    </button>
  );
}

export default function MorePage() {
  const navigate   = useNavigate();
  const { isEnabled } = useServices();
  const [query, setQuery] = useState("");

  // Filter groups and services against both isEnabled flags and search query
  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SERVICE_GROUPS
      .filter(g => !g.serviceKey || isEnabled(g.serviceKey))
      .map(g => ({
        ...g,
        services: g.services.filter(s => {
          if (s.serviceKey && !isEnabled(s.serviceKey)) return false;
          if (q) return s.label.toLowerCase().includes(q) || g.title.toLowerCase().includes(q);
          return true;
        }),
      }))
      .filter(g => g.services.length > 0);
  }, [query, isEnabled]);

  return (
    <div className="min-h-screen bg-bg pb-28">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <h1 className="text-primary font-bold text-lg font-display">All Services</h1>
          <div className="w-9" /> {/* spacer */}
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2.5 bg-surface-muted border border-border rounded-2xl px-4 py-3 focus-within:border-brand transition-colors">
          <i className="ri-search-line text-muted text-base flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search services…"
            className="flex-1 bg-transparent text-sm text-primary outline-none placeholder:text-faint"
          />
          {query.length > 0 && (
            <button onClick={() => setQuery("")} className="text-muted">
              <i className="ri-close-line text-base" />
            </button>
          )}
        </div>
      </div>

      <div className="px-5 pt-5 space-y-6 pb-6">

        {/* ── Quick Provider Logos ──────────────────────────── */}
        {!query && (
          <div>
            <p className="text-primary font-bold text-sm font-display mb-3">Quick Access</p>
            <div className="bg-surface rounded-2xl border border-border shadow-card px-4 py-4">
              <div className="grid grid-cols-6 gap-2">
                {QUICK_PROVIDERS.map(p => (
                  <button key={p.label} onClick={() => navigate(p.path)}
                    className="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform">
                    <div className="w-11 h-11 rounded-xl bg-surface-muted border border-border flex items-center justify-center overflow-hidden group-hover:border-brand-border transition-colors">
                      <img src={p.img} alt={p.label} className="w-7 h-7 object-contain"
                        onError={e => { e.target.style.display = "none"; }} />
                    </div>
                    <span className="text-muted text-[9px] font-semibold">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Service Groups ────────────────────────────────── */}
        {visibleGroups.length > 0 ? (
          visibleGroups.map(group => (
            <div key={group.id}>
              <p className="text-primary font-bold text-sm font-display mb-3">{group.title}</p>
              <div className="grid grid-cols-4 gap-2.5">
                {group.services.map(s => (
                  <ServiceTile key={s.path} service={s} onNavigate={navigate} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-surface-muted rounded-2xl flex items-center justify-center mx-auto mb-3">
              <i className="ri-search-line text-muted text-2xl" />
            </div>
            <p className="text-secondary font-semibold text-sm mb-1">No services found</p>
            <p className="text-muted text-xs">Try a different search term</p>
          </div>
        )}

        {/* ── Promo / Marketing slides ─────────────────────── */}
        {!query && (
          <div>
            <p className="text-primary font-bold text-sm font-display mb-3">Promotions</p>
            <MarketingSlides page="more" />
          </div>
        )}

      </div>

      <MobileBottomNav />
    </div>
  );
}
